import type { Schema } from '@dealdesk/api-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { FormErrorSummary, M, SelectField, TextareaField, applyServerErrors, useConfigLabel, zRequiredString } from '@/business';
import { Button, Dialog, DialogContent, toast } from '@/design-system';
import { apiErrorMessage, isApiError, type ApiError } from '@/shared/api';

import { useRequestEnlistment, useUpdateEnlistment } from '../api/mutations';
import { employerCategoriesQuery, lookupsQuery } from '../api/queries';

/** Opens an enlistment request with a bank (employer not on the bank's list). */
export function RequestEnlistmentDialog({ employer, onClose }: { employer: Schema<'Employer'>; onClose: () => void }) {
  const { t } = useTranslation('employers');
  const lookups = useQuery(lookupsQuery);
  const request = useRequestEnlistment(employer.id);
  const [problem, setProblem] = useState<ApiError | null>(null);
  const form = useForm<{ bankId: string; note?: string }>({ resolver: zodResolver(z.object({ bankId: zRequiredString(), note: z.string().optional() })), defaultValues: { bankId: '', note: '' } });
  const listedAt = new Set(employer.classifications.filter((c) => c.listed).map((c) => c.bankId));
  const submit = form.handleSubmit(async (v) => {
    setProblem(null);
    try {
      await request.mutateAsync({ bankId: v.bankId, ...(v.note?.trim() ? { note: v.note.trim() } : {}) });
      toast.done(t('enlistments.requestedDone'));
      onClose();
    } catch (e) {
      if (isApiError(e)) {
        applyServerErrors(form, e, ['bankId']);
        setProblem(e);
      }
    }
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title={t('enlistments.requestTitle', { name: employer.name })}
        description={t('enlistments.requestBody')}
        footer={
          <>
            <Button onClick={onClose}>{t('actions.cancel', { ns: 'common' })}</Button>
            <Button variant="primary" loading={request.isPending} onClick={() => void submit()}>
              {t('record.requestEnlistment')}
            </Button>
          </>
        }
      >
        <form onSubmit={(e) => void submit(e)} noValidate className="flex flex-col gap-4">
          {problem ? <FormErrorSummary title={apiErrorMessage(problem)} /> : null}
          <SelectField control={form.control} name="bankId" label={t('classify.bank')} options={(lookups.data?.banks ?? []).filter((b) => !listedAt.has(b.id)).map((b) => ({ value: b.id, label: `${b.code} · ${b.name}` }))} placeholder={t('form.selectPlaceholder', { ns: 'common' })} />
          <TextareaField control={form.control} name="note" label={t('classify.note')} optional rows={2} />
          <button type="submit" hidden />
        </form>
      </DialogContent>
    </Dialog>
  );
}

const STATUSES: Schema<'EnlistmentStatus'>[] = ['requested', 'documents_pending', 'submitted_to_bank', 'enlisted', 'declined'];
const statusSchema = z
  .object({ status: z.enum(['requested', 'documents_pending', 'submitted_to_bank', 'enlisted', 'declined']), categoryCode: z.string().optional(), note: z.string().optional() })
  .superRefine((v, ctx) => {
    if (v.status === 'enlisted' && !v.categoryCode) ctx.addIssue({ code: 'custom', path: ['categoryCode'], message: M.required });
  });

/** Moves an enlistment request on. When enlisted, the bank's category becomes the employer's classification. */
export function EnlistmentStatusDialog({ request, onClose }: { request: Schema<'EnlistmentRequest'>; onClose: () => void }) {
  const { t } = useTranslation('employers');
  const update = useUpdateEnlistment();
  const categories = useQuery(employerCategoriesQuery(request.bankId));
  const configLabel = useConfigLabel();
  const [problem, setProblem] = useState<ApiError | null>(null);
  const form = useForm<z.infer<typeof statusSchema>>({ resolver: zodResolver(statusSchema), defaultValues: { status: request.status, categoryCode: '', note: '' } });
  const status = useWatch({ control: form.control, name: 'status' });
  const submit = form.handleSubmit(async (v) => {
    setProblem(null);
    try {
      await update.mutateAsync({ id: request.id, body: { status: v.status, ...(v.categoryCode ? { categoryCode: v.categoryCode } : {}), ...(v.note?.trim() ? { note: v.note.trim() } : {}) } });
      toast.done(t('enlistments.updated'));
      onClose();
    } catch (e) {
      if (isApiError(e)) {
        applyServerErrors(form, e, ['categoryCode']);
        setProblem(e);
      }
    }
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title={t('enlistments.updateTitle', { name: request.employerName, bank: request.bankCode })}
        footer={
          <>
            <Button onClick={onClose}>{t('actions.cancel', { ns: 'common' })}</Button>
            <Button variant="primary" loading={update.isPending} onClick={() => void submit()}>
              {t('enlistments.update')}
            </Button>
          </>
        }
      >
        <form onSubmit={(e) => void submit(e)} noValidate className="flex flex-col gap-4">
          {problem ? <FormErrorSummary title={apiErrorMessage(problem)} /> : null}
          <SelectField control={form.control} name="status" label={t('enlistments.status')} options={STATUSES.map((s) => ({ value: s, label: t(`enlistment.${s}`, { ns: 'status' }) }))} />
          {status === 'enlisted' ? <SelectField control={form.control} name="categoryCode" label={t('enlistments.assignedCategory', { bank: request.bankCode })} options={(categories.data ?? []).filter((c) => c.listed).map((c) => ({ value: c.code, label: configLabel(c.label, c.labelAr) }))} placeholder={t('form.selectPlaceholder', { ns: 'common' })} /> : null}
          <TextareaField control={form.control} name="note" label={t('classify.note')} optional rows={2} />
          <button type="submit" hidden />
        </form>
      </DialogContent>
    </Dialog>
  );
}
